import { fetchAuthSession } from '@aws-amplify/auth';
import { BACKEND_AGENTS_API, BACKEND_SEMANTIC_WORKSPACES_API } from '../env';

// SQL Workbook (2026-09-23): el CRUD de hojas guardadas vive en
// whisperbi-back-semantic-model (junto con el resto del módulo de
// workspace), pero la EJECUCIÓN de la consulta contra Athena vive en
// whisperbi-agent-management-strands -- mismo patrón ya usado para el
// historial de snapshots del Catálogo Iceberg (Fase 5.1): dos backends
// distintos, un solo service en el frontend.
const EXECUTE_API = `${BACKEND_AGENTS_API}/sql-workbook/execute`;

async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();
    if (idToken) {
      return { Authorization: `Bearer ${idToken}` };
    }
  } catch (error) {
    console.error('SqlWorkbookService.getAuthHeader: unable to fetch session', error);
  }
  return {};
}

async function fetchWithAuth(input, init = {}) {
  const authHeader = await getAuthHeader();
  const headers = {
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers || {}),
    ...authHeader
  };

  const response = await fetch(input, { ...init, headers, mode: 'cors', cache: 'no-cache' });

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${errorBody || 'Unexpected error'}`);
  }
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text || null;
  }
}

/**
 * Cliente del SQL Workbook: hojas de consulta SQL guardadas (privadas por
 * usuario, ver sql_workbook_sheet/app.py) dentro de un semantic_workspace,
 * y ejecución de esas consultas contra Athena, acotada a las bases
 * Iceberg del tenant y validada de solo-lectura (mismos guards que ya
 * protegen al agente LLM, ver src/sql-workbook/app.py).
 */
const SqlWorkbookService = {
  async listSheets(workspaceId) {
    if (!workspaceId) {
      throw new Error('SqlWorkbookService.listSheets: workspaceId is required');
    }
    return fetchWithAuth(`${BACKEND_SEMANTIC_WORKSPACES_API}/${encodeURIComponent(workspaceId)}/sql-workbook`, { method: 'GET' });
  },

  async getSheet(workspaceId, sheetId) {
    if (!workspaceId || !sheetId) {
      throw new Error('SqlWorkbookService.getSheet: workspaceId and sheetId are required');
    }
    const url = `${BACKEND_SEMANTIC_WORKSPACES_API}/${encodeURIComponent(workspaceId)}/sql-workbook/${encodeURIComponent(sheetId)}`;
    return fetchWithAuth(url, { method: 'GET' });
  },

  async createSheet(workspaceId, { name, sqlText, icebergDatabaseId, icebergSchemaId } = {}) {
    if (!workspaceId || !name) {
      throw new Error('SqlWorkbookService.createSheet: workspaceId and name are required');
    }
    return fetchWithAuth(`${BACKEND_SEMANTIC_WORKSPACES_API}/${encodeURIComponent(workspaceId)}/sql-workbook`, {
      method: 'POST',
      body: JSON.stringify({
        name,
        sql_text: sqlText || '',
        iceberg_database_id: icebergDatabaseId || null,
        iceberg_schema_id: icebergSchemaId || null
      })
    });
  },

  async updateSheet(workspaceId, sheetId, { name, sqlText, icebergDatabaseId, icebergSchemaId } = {}) {
    if (!workspaceId || !sheetId) {
      throw new Error('SqlWorkbookService.updateSheet: workspaceId and sheetId are required');
    }
    const body = {};
    if (name !== undefined) body.name = name;
    if (sqlText !== undefined) body.sql_text = sqlText;
    if (icebergDatabaseId !== undefined) body.iceberg_database_id = icebergDatabaseId;
    if (icebergSchemaId !== undefined) body.iceberg_schema_id = icebergSchemaId;

    const url = `${BACKEND_SEMANTIC_WORKSPACES_API}/${encodeURIComponent(workspaceId)}/sql-workbook/${encodeURIComponent(sheetId)}`;
    return fetchWithAuth(url, { method: 'PUT', body: JSON.stringify(body) });
  },

  async deleteSheet(workspaceId, sheetId) {
    if (!workspaceId || !sheetId) {
      throw new Error('SqlWorkbookService.deleteSheet: workspaceId and sheetId are required');
    }
    const url = `${BACKEND_SEMANTIC_WORKSPACES_API}/${encodeURIComponent(workspaceId)}/sql-workbook/${encodeURIComponent(sheetId)}`;
    return fetchWithAuth(url, { method: 'DELETE' });
  },

  // ---- Ejecución (whisperbi-agent-management-strands) -------------------
  async executeQuery(glueDatabase, sqlQuery, { outputLimit } = {}) {
    if (!glueDatabase || !sqlQuery) {
      throw new Error('SqlWorkbookService.executeQuery: glueDatabase and sqlQuery are required');
    }
    const body = { glue_database: glueDatabase, sql_query: sqlQuery };
    if (outputLimit) {
      body.output_limit = outputLimit;
    }
    return fetchWithAuth(EXECUTE_API, { method: 'POST', body: JSON.stringify(body) });
  }
};

export default SqlWorkbookService;
